import { lazy, Suspense, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import TagManager from "react-gtm-module";
import SEO from "../SEO";
import FeaturesGrid from "./FeaturesGrid";
import styles from "./Home.module.css";
import SavingsCalculator from "./SavingsCalculator";
import WhyWaterless from "./WhyWaterless";

// Lazy load below-the-fold components
const ProjectsGallery = lazy(() => import("./ProjectsGallery"));
const CustomerReviews = lazy(() => import("../CustomerReviews"));
const ContainerHomeForm = lazy(() => import("../EmailForm/ContainerHomeForm/ContainerHomeForm"));

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ווטרלס - Waterless",
  url: "https://waterless.co.il",
  logo: "https://waterless.co.il/assets/images/waterlessLogo.png",
  description:
    "משתנות ללא מים מבית Waterless — המותג המוביל בעולם. חיסכון של עד 100,000 ליטר מים בשנה למשתנה. פתרון ירוק וחסכוני לעסקים ומוסדות בישראל.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "צבי רוזנבלט 19",
    addressLocality: "תל אביב",
    postalCode: "68151",
    addressCountry: "IL",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+972-54-4494384",
    contactType: "customer service",
    availableLanguage: ["Hebrew", "English"],
  },
  email: "waterless.israel@gmail.com",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "מה זה משתנה ללא מים ואיך זה עובד?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "משתנה ללא מים היא מערכת שירותים חדשנית שאינה דורשת מים להדחה. המשתנה עשויה מחרס דוחה נוזלים ומצוידת בסיפון מיוחד עם נוזל איטום שמונע ריחות. השתן עובר דרך נוזל האיטום ומתנקז ישירות למערכת הביוב ללא צורך בשטיפה.",
      },
    },
    {
      "@type": "Question",
      name: "האם משתנות ללא מים מריחות?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "לא. משתנות ללא מים מצוידות בסיפון ייחודי עם נוזל איטום שיוצר מחסום הרמטי המונע עליית ריחות. למעשה, משתנות ללא מים לרוב מריחות פחות ממשתנות רגילות כי אין מים עומדים שמהווים קרקע לחיידקים.",
      },
    },
    {
      "@type": "Question",
      name: "מה התחזוקה הנדרשת למשתנות ללא מים?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "התחזוקה מינימלית — יש להחליף את מחסנית נוזל האיטום בממוצע פעם בכמה חודשים (תלוי בעומס השימוש), ולנקות את המשתנה בניקוי שגרתי. אין חלקים מכאניים, שסתומי שטיפה או חיבורי חשמל שדורשים תחזוקה.",
      },
    },
    {
      "@type": "Question",
      name: "למי מתאימות משתנות ללא מים?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "משתנות ללא מים מתאימות למגוון רחב של מבנים: משרדים, קניונים, מוסדות ציבור, בתי ספר, אצטדיונים, מפעלים, מסעדות ועוד. הן מתאימות במיוחד למבנים ירוקים העומדים בתקני LEED ולעסקים שמעוניינים להפחית עלויות תפעול.",
      },
    },
    {
      "@type": "Question",
      name: "האם משתנה ללא מים כשרה לשבת?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "כן. משתנות ללא מים של Falcon פועלות על עקרון הכבידה בלבד — ללא חשמל, ללא חיישנים וללא כל מנגנון חשמלי. לכן השימוש בהן מותר לחלוטין בשבת ובחג, ללא חשש מחילול שבת. זהו יתרון משמעותי למוסדות, בתי כנסת, מלונות ועסקים שומרי מסורת.",
      },
    },
    {
      "@type": "Question",
      name: "האם מגיעה אחריות עם המשתנות?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "כן. כל משתנה כוללת אחריות יצרן מלאה. ווטרלס, היבואן המורשה של Falcon בישראל, מספקת שירות, אספקת חלקים ותחזוקה בכל רחבי הארץ — כך שאתם מקבלים גם מוצר עולמי וגם גיבוי מקומי.",
      },
    },
  ],
};

const productCatalogSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "משתנות ללא מים Waterless",
  description:
    "משתנות ללא מים מבית Waterless עם טכנולוגיית Falcon Velocity — חיסכון של עד 100,000 ליטר מים בשנה, ללא חשמל, תחזוקה מינימלית והיגיינה גבוהה.",
  url: "https://waterless.co.il/",
  numberOfItems: 3,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "פילטר Falcon Velocity™",
      url: "https://waterless.co.il/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "משתנה ללא מים - דגם קלאסי",
      url: "https://waterless.co.il/",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "משתנה ללא מים - דגם מתקדם",
      url: "https://waterless.co.il/",
    },
  ],
};

const products = [
  {
    id: "falcon-filter",
    title: "פילטר Falcon Velocity™",
    description: "מסנן חכם שמונע ריחות והתזות ושומר על ניקיון מקסימלי.",
    image: "./assets/images/Falcon Velocity.png",
    alt: "פילטר Falcon Velocity למניעת ריחות והתזות",
  },
  {
    id: "urinal-model-1",
    title: "משתנה ללא מים - דגם קלאסי",
    description: "עיצוב נקי עם זרימה חלקה, מתאים למוסדות ולעסקים בכל גודל.",
    image: "./assets/images/urinal-1 1.png",
    alt: "משתנה ללא מים מבית Falcon Velocity",
  },
  {
    id: "urinal-model-2",
    title: "משתנה ללא מים - דגם מתקדם",
    description: "דגם קומפקטי עם תחזוקה מינימלית וחיסכון מקסימלי במים.",
    image: "./assets/images/urinal-1 2.png",
    alt: "משתנה ללא מים דגם נוסף מבית Falcon Velocity",
  },
];

const ProductCard = ({ product }) => (
  <article className={styles.productCard}>
    <div className={styles.productImageWrapper}>
      <img src={product.image} alt={product.alt} className={styles.productImage} loading="lazy" decoding="async" />
    </div>
    <div className={styles.productCardBody}>
      <h3 className={styles.productCardTitle}>{product.title}</h3>
      <p className={styles.productCardDescription}>{product.description}</p>
    </div>
  </article>
);

function Home({ title }) {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    TagManager.dataLayer({ dataLayer: { event: "pageview", pagePath: "/home", pageTitle: title } });
  }, [title]);
  const requestQuote = () => navigate("/contact");

  return (
    <main className={styles.home} dir="rtl">
      <SEO
        title="משתנות ללא מים ושירותים ירוקים לעסק | ווטרלס (Waterless) ישראל"
        description="שירותים ירוקים ללא מים לעסק: משתנות Waterless (היבואן המורשה של Falcon) חוסכות עד 100,000 ליטר מים בשנה. פתרון ירוק וחסכוני, ללא חשמל, כשר לשבת, אחריות מלאה ושירות ארצי. בקשו הצעת מחיר!"
        path="/"
        structuredData={[organizationSchema, faqSchema, productCatalogSchema]}
      />
      <section className={styles.heroSection} aria-labelledby="home-title">
        <div className={styles.heroRow}>
          <div className={styles.heroContent}>
            <p className={styles.eyebrow}>ווטרלס · היבואן המורשה של Falcon בישראל</p>
            <h1 id="home-title" className={styles.heroTitle}>משתנות ללא מים לעסקים ולמוסדות</h1>
            <p className={styles.heroDescription}>פתרון למשרדים, מוסדות ומבני ציבור: חיסכון במי הדחה, ללא חיבור לחשמל, עם שירות וחלקים בישראל.</p>
            <ul className={styles.heroHighlights}>
              {["ללא מים להדחה", "ללא חשמל", "שירות וחלקים בישראל"].map(benefit => <li key={benefit}><span aria-hidden="true">✓</span> {benefit}</li>)}
            </ul>
            <div className={styles.heroActions}>
              <Link className={styles.primaryCTA} to="/contact">בקשת הצעת מחיר</Link>
              <a className={styles.secondaryCTA} href="https://wa.me/972525970820" target="_blank" rel="noopener noreferrer">דברו בוואטסאפ</a>
            </div>
            <p className={styles.trustNote}>היבואן המורשה של Falcon בישראל</p>
          </div>
          <img className={styles.heroImage} src="/assets/images/hero-urinals.webp" alt="ארבע משתנות ללא מים לבנות עם מחיצות פרטיות בחדר שירותים בהיר" width="1672" height="941" loading="lazy" />
        </div>
      </section>
      <Suspense fallback={<p className={styles.loadingFallback} role="status">טוען תמונות התקנה...</p>}><ProjectsGallery /></Suspense>
      <section className={styles.section} aria-labelledby="products-title">
        <div className={styles.container}>
          <h2 id="products-title" className={styles.sectionTitle}>המוצרים שלנו</h2>
          <p className={styles.sectionDescription}>משתנות ווטרלס של פלקון ופתרונות סינון ותחזוקה למשרדים, מוסדות ומבני ציבור.</p>
          <div className={styles.productsGrid}>{products.map(product => <ProductCard key={product.id} product={product} />)}</div>
          <Link className={styles.primaryCTA} to="/contact">בקשת הצעת מחיר</Link>
        </div>
      </section>
      <FeaturesGrid />
      <SavingsCalculator onCtaClick={requestQuote} />
      <WhyWaterless onCtaClick={requestQuote} />
      <Suspense fallback={<p className={styles.loadingFallback} role="status">טוען חוות דעת...</p>}><CustomerReviews /></Suspense>
      <section className={styles.section} aria-labelledby="faq-title">
        <div className={styles.container}>
          <h2 id="faq-title" className={styles.sectionTitle}>שאלות נפוצות</h2>
          {faqSchema.mainEntity.map(question => (
            <details className={styles.faqItem} key={question.name}>
              <summary>{question.name}</summary>
              <p>{question.acceptedAnswer.text}</p>
            </details>
          ))}
        </div>
      </section>
      <div className={styles.resources}>
        <div className={styles.resourceRow}>
          <Link to="/שירותים-ירוקים">שירותים חסכוניים לעסק ירוק</Link>
          <Link to="/השוואת-משתנות-ללא-מים">השוו בין הטכנולוגיות של משתנות ללא מים</Link>
          <Link to="/article/מה-הם-שירותים-ירוקים-לעסק">מה הם שירותים ירוקים</Link>
        </div>
      </div>
      <Suspense fallback={<p className={styles.loadingFallback} role="status">טוען טופס...</p>}><ContainerHomeForm /></Suspense>
    </main>
  );
}

export default Home;
